import { useCallback, useEffect, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import {
  endTickerDrag,
  isTickerDrag,
  markTickerTarget,
  readTickerDrop,
  useTickerDragStore,
} from './ticker-drag'

export interface TickerDropTarget {
  /** A ticker drag is in progress anywhere (show the faint outline). */
  isDragging: boolean
  /** It is over THIS target (show the label). */
  isOver: boolean
  /** The ticker being dragged, for the label. */
  ticker: string | null
  /** Spread onto the target's root element. */
  dropHandlers: {
    onDragEnter: (e: DragEvent) => void
    onDragOver: (e: DragEvent) => void
    onDragLeave: (e: DragEvent) => void
    onDrop: (e: DragEvent) => void
  }
}

/**
 * Make an element accept dropped tickers (see ticker-drag.ts). Only ticker
 * drags are touched; every other drag (panels, layout tabs, files) passes
 * through untouched.
 */
export function useTickerDropTarget(
  onDropTicker: (ticker: string) => void,
  enabled = true,
): TickerDropTarget {
  const ticker = useTickerDragStore((s) => s.ticker)
  const [isOver, setIsOver] = useState(false)
  // dragenter/leave fire for every child crossed; count them so moving over
  // the target's own children doesn't flicker the highlight.
  const depthRef = useRef(0)
  const onDropRef = useRef(onDropTicker)
  onDropRef.current = onDropTicker

  // A drag that ends anywhere (Escape, dropped elsewhere) resets the target.
  useEffect(() => {
    if (ticker === null) {
      depthRef.current = 0
      setIsOver(false)
    }
  }, [ticker])

  const accepts = useCallback((e: DragEvent) => enabled && isTickerDrag(e.dataTransfer), [enabled])

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return
      e.preventDefault()
      depthRef.current += 1
      setIsOver(true)
    },
    [accepts],
  )

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
      markTickerTarget(e.nativeEvent)
    },
    [accepts],
  )

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return
      depthRef.current = Math.max(0, depthRef.current - 1)
      if (depthRef.current === 0) setIsOver(false)
    },
    [accepts],
  )

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!accepts(e)) return
      // No stopPropagation: the react-dnd backend's window drop handler must
      // still see the drop to end its native-drag state.
      e.preventDefault()
      depthRef.current = 0
      setIsOver(false)
      const dropped = readTickerDrop(e.dataTransfer)
      endTickerDrag()
      if (dropped) onDropRef.current(dropped)
    },
    [accepts],
  )

  return {
    isDragging: enabled && ticker !== null,
    isOver: enabled && isOver,
    ticker,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  }
}
